import React, { useState, useEffect } from "react";
import { DashboardService } from "../../services/dashboard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const getCategory = (action) => {
  if (
    [
      "update_lesson_progress",
      "create_lesson",
      "badge_awarded",
      "auto_assign_lesson",
      "training_overdue",
    ].includes(action)
  )
    return "training";
  if (
    [
      "dish_created",
      "dish_updated",
      "dish_deleted",
      "dish_restored",
      "wine_created",
      "wine_updated",
      "wine_deleted",
      "wine_restored",
    ].includes(action)
  )
    return "menu";
  if (
    [
      "user_registered",
      "login",
      "login_failed",
      "logout",
      "user_deactivated",
      "password_reset_requested",
      "create_restaurant_success",
    ].includes(action)
  )
    return "staff";
  return "other";
};

const getIconStyles = (action) => {
  const category = getCategory(action);

  // Default fallback
  let styles = {
    bg: "bg-gray-500",
    iconColor: "text-white",
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
  };

  // 1. Training - RED
  if (category === "training") {
    styles.bg = "bg-[#D32F2F]";
    styles.iconColor = "text-white";
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
        <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
      </svg>
    ); // Graduation
  }

  // 2. Menu - GOLD
  if (category === "menu") {
    styles.bg = "bg-[#EAB308]";
    styles.iconColor = "text-white";
    // Default to Burger/Utensils for generic menu (matches previous file's 'utensils')
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 10c0-3 4-5 8-5s8 2 8 5"></path>
        <path d="M4 13h16"></path>
        <path d="M4 16c0 1.5 4 2 8 2s8-.5 8-2"></path>
      </svg>
    );
  }

  // 3. Staff - ORANGE
  if (category === "staff") {
    styles.bg = "bg-[#F97316]";
    styles.iconColor = "text-white";
    // Default User
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    );
  }

  return styles;
};

// Refine icon content based on specific action if needed overrides general category
const getSpecificIcon = (action) => {
  const styles = getIconStyles(action);

  // Wines -> Wine Glass
  if (action.includes("wine")) {
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 22h8"></path>
        <path d="M12 11v11"></path>
        <path d="M17 3H7l5 8 5-8Z"></path>
      </svg>
    )
  }

  // New User -> User Plus
  if (action === "user_registered") {
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <line x1="20" y1="8" x2="20" y2="14"></line>
        <line x1="23" y1="11" x2="17" y2="11"></line>
      </svg>
    )
  }

  // Login -> Log In Icon
  if (action === "login") {
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
        <polyline points="10 17 15 12 10 7"></polyline>
        <line x1="15" x2="3" y1="12" y2="12"></line>
      </svg>
    )
  }

  // Badge -> Award
  if (action === "badge_awarded") {
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="7"></circle>
        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
      </svg>
    )
  }

  // Alerts
  if (action === 'training_overdue' || action === 'login_failed' || action === 'alert-circle') {
    styles.bg = "bg-[#D32F2F]";
    styles.icon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
        <path d="M12 9v4"></path>
        <path d="M12 17h.01"></path>
      </svg>
    )
  }

  return styles;
}


const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export default function ActivityFeed({ logs = [], loading: parentLoading }) {
  const [loading, setLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [filter, setFilter] = useState("all"); // all, training, menu, staff
  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10



  // fetch logs from API
  useEffect(() => {
    let mounted = true;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const data = await DashboardService.getActivityLogs(
          currentPage,
          itemsPerPage,
          filter
        );

        if (!mounted) return;

        setActivityLogs(data.logs || []);
        setTotalPages(data.pagination?.totalPages || 1);
      } catch (err) {
        console.error("Failed to fetch activity logs:", err);
        setActivityLogs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLogs();
    return () => {
      mounted = false;
    };
  }, [currentPage, filter]);


  const isLoading = parentLoading || loading;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const generateLogTitle = (log) => {
    const details = log.details || {};
    const action = log.action;
    const user = log.user_uuid ? `${log.user_uuid.first_name} ${log.user_uuid.last_name}` : (details.user_role || "User");

    if (action === "update_lesson_progress") {
      // details.lesson is attached by the logs API ("Beer Unit 1, Chapter 2: ...").
      const lesson = details.lesson;
      if (!lesson) return `${user} completed a lesson module`;
      const category = lesson.category ? lesson.category.charAt(0).toUpperCase() + lesson.category.slice(1) : "";
      const chapter = `${category} Unit ${lesson.unit}, Chapter ${lesson.chapter}${lesson.chapter_name ? `: ${lesson.chapter_name}` : ""}`.trim();
      // Older entries record completion as chapterPassed.
      const completed = details.isCompleted ?? details.chapterPassed;
      return `${user} ${completed ? "completed" : "worked on"} ${chapter}`;
    }
    if (action === "badge_awarded") return `${user} earned ${details.badge_name || 'a badge'}`;
    if (action === "user_registered") return `New employee added: ${details.role || 'Staff'}`;
    if (action === "dish_created") return `${details.description || 'Unknown'}`;
    if (action === "wine_created") return `${details.description || 'Unknown'}`;
    if (action === "dish_updated") return `${details?.description}`;
    if (action === "wine_updated") return `${details?.description}`;
    if (action === "login") return `${user} logged in`;

    return details.description || action.replace(/_/g, " ");
  }

  // Activity Feed Filters
  const filters = [
    { id: "training", label: "Training", color: "text-red-700", icon: "" }, // Using emoji as placeholder or simple styling
    { id: "menu", label: "Menu Updates", color: "text-yellow-700", icon: "" },
    { id: "staff", label: "Staff", color: "text-orange-700", icon: "" },
    { id: "all", label: "All Events", color: "text-green-900 bg-green-100", icon: "📋" }
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 h-full flex flex-col">
      <h3 className="text-xl font-bold text-[#881337] mb-6 text-left">
        Location Activity Feed
      </h3>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { id: "training", label: "Training", img: "/training.png", styles: { active: "bg-red-50 border-red-200 text-red-700" } },
          { id: "menu", label: "Menu Updates", img: "/menuupdates.png", styles: { active: "bg-yellow-50 border-yellow-200 text-yellow-700" } },
          { id: "staff", label: "Staff", img: "/staff.png", styles: { active: "bg-orange-50 border-orange-200 text-orange-700" } },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors shrink-0 ${filter === f.id
              ? f.styles.active
              : 'bg-white border-gray-100 text-gray-600 hover:bg-gray-50'
              }`}
          >
            <img src={f.img} alt={f.label} className="w-5 h-5 object-contain" />
            {f.label}
          </button>
        ))}

        <button
          onClick={() => setFilter("all")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors shrink-0 ${filter === 'all' ? 'bg-green-100 border-green-200 text-green-800' : 'bg-white border-gray-100 text-gray-600 hover:bg-gray-50'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
          All Events
        </button>
      </div>
      <hr className="border-gray-100 mb-6" />

      <div className="flex-1">
        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">No activity found for this category</div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-6">
              {activityLogs.map((log) => {
                const styles = getSpecificIcon(log.action);
                return (
                  <div key={log._id} className="flex gap-4 items-start animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${styles.bg} ${styles.iconColor} shadow-sm`}>
                      {styles.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-gray-800 text-left leading-tight mb-0.5 mt-0.5">
                        {generateLogTitle(log)}
                      </h4>
                      <p className="text-xs text-gray-400 text-left">
                        {log?.restaurant_uuid?.name}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap pt-1">
                      {formatTimeAgo(log.timestamp)}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-6 border-t border-gray-50 mt-auto">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg border transition-all duration-300 ${currentPage === 1
                      ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-200 text-[#881337] hover:border-[#881337] hover:bg-[#881337]/5 shadow-sm"
                      }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg border transition-all duration-300 ${currentPage === totalPages
                      ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-200 text-[#881337] hover:border-[#881337] hover:bg-[#881337]/5 shadow-sm"
                      }`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
